import React, { useState } from 'react';
import { Check, ArrowRight, HelpCircle, Search, Sunrise, Sprout, TreePine, Crown } from 'lucide-react';

const journeyStages = [
  {
    id: 'curious',
    name: 'Just Curious',
    icon: HelpCircle,
    description: 'You have questions about faith and spirituality. You want to explore without pressure.',
    features: [
      'No-pressure exploration',
      'Ask any question',
      'Discover at your pace',
      'Welcoming community'
    ],
    color: 'from-slate-400 to-slate-600'
  },
  {
    id: 'agnostic',
    name: 'Agnostic',
    icon: Search,
    description: "You're open to possibilities but need evidence and honest dialogue about life's biggest questions.",
    features: [
      'Evidence-based discussions',
      'Honest dialogue',
      'Intellectual respect',
      'No judgment zone'
    ],
    color: 'from-blue-400 to-blue-600'
  },
  {
    id: 'sensing',
    name: 'Feeling God Exists',
    icon: Sunrise,
    description: "You sense there's something more. You're beginning to believe but have lingering questions.",
    features: [
      'Guided exploration',
      'Personal testimonies',
      'Gentle introduction',
      'Prayer support'
    ],
    color: 'from-amber-400 to-orange-500'
  },
  {
    id: 'new-believer',
    name: 'New Believer',
    icon: Sprout,
    description: "You've taken the first step of faith. Now you need strong foundations to grow.",
    features: [
      'Foundational teaching',
      'Mentorship connections',
      'Practical application',
      'Community support'
    ],
    color: 'from-emerald-400 to-green-600'
  },
  {
    id: 'seasoned',
    name: 'Seasoned Believer',
    icon: TreePine,
    description: "You've walked with God for years. You're ready for deeper revelations and greater impact.",
    features: [
      'Advanced teachings',
      'Deeper revelations',
      'Leadership development',
      'Kingdom strategies'
    ],
    color: 'from-teal-400 to-teal-600'
  },
  {
    id: 'minister',
    name: 'Minister',
    icon: Crown,
    description: "You're called to serve others. You need equipping and resources for effective ministry.",
    features: [
      'Ministry resources',
      'Leadership tools',
      'Network connections',
      'Strategic support'
    ],
    color: 'from-purple-400 to-purple-600'
  }
];

const JourneySection = () => {
  const [activeStage, setActiveStage] = useState('new-believer');
  const currentStage = journeyStages.find(s => s.id === activeStage);
  const Icon = currentStage?.icon || Sprout;

  return (
    <section className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1 bg-purple-50 text-purple-600 rounded-full text-sm font-medium mb-4">
            Meet You Where You Are
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Where are you on your
            <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent"> spiritual journey?</span>
          </h2>
          <p className="text-lg text-slate-600">
            We have resources and community tailored specifically for wherever you are right now.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Journey stages selector */}
          <div className="lg:col-span-2 space-y-2">
            {journeyStages.map((stage) => {
              const StageIcon = stage.icon;
              return (
                <button
                  key={stage.id}
                  onClick={() => setActiveStage(stage.id)}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl text-left transition-all duration-300 ${
                    activeStage === stage.id
                      ? 'bg-white shadow-lg border-2 border-indigo-200'
                      : 'bg-transparent hover:bg-white hover:shadow border-2 border-transparent'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stage.color} flex items-center justify-center flex-shrink-0`}>
                    <StageIcon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-grow">
                    <h3 className={`font-semibold ${
                      activeStage === stage.id ? 'text-slate-900' : 'text-slate-700'
                    }`}>
                      {stage.name}
                    </h3>
                    {activeStage === stage.id && (
                      <p className="text-sm text-slate-500 mt-1 animate-in fade-in duration-300">
                        Click to explore
                      </p>
                    )}
                  </div>
                  {activeStage === stage.id && (
                    <ArrowRight className="w-5 h-5 text-indigo-600" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Active stage details */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
              <div className={`bg-gradient-to-br ${currentStage?.color} p-8`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">{currentStage?.name}</h3>
                    <span className="text-white/80 text-sm">Tailored for your journey</span>
                  </div>
                </div>
                <p className="text-white/90 text-lg">
                  {currentStage?.description}
                </p>
              </div>
              
              <div className="p-8">
                <h4 className="font-semibold text-slate-900 mb-4">What you&apos;ll find:</h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  {currentStage?.features.map((feature, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                      <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Check className="w-4 h-4 text-emerald-600" />
                      </div>
                      <span className="text-slate-700">{feature}</span>
                    </div>
                  ))}
                </div>
                
                <button className="mt-8 w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transition-shadow flex items-center justify-center gap-2 group">
                  Start Your Journey Here
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JourneySection;
